import { z } from 'zod'

import {
  AcademySchema,
  AdditionalPricingPlanSchema,
  SubscribePlanSchema,
} from '~/@entities/Academy'
import { SmsSchema } from '~/@entities/Sms'
import { StudentSchema } from '~/@entities/Student'
import { sliceSchema } from '~/schema/slice'

const _localDatetime = z.iso.datetime({ local: true })

/***************** 청구 (billing 서버 기준) *****************/
// 아래 분류·상태·종류 값은 billing 서버가 정하고 메인 서버가 문자열로 옮겨 싣는다. 메인 서버도 enum 으로 묶지 않아
// 닫힌 목록으로 좁히지 않는다. 금액은 모두 원 단위다.

export const BillingAdjustmentSchema = z.object({
  id: z.number(),
  /** PENALTY(위약금)·MANUAL_ADJUSTMENT(수기 조정) */
  kind: z.string(),
  /** 차감이면 음수다 */
  amount: z.number(),
  reason: z.string(),
  /** 수기 조정 메모. 위약금은 항상 null 이다 */
  memo: z.string().nullable(),
  /** null 이면 요금 항목에 붙지 않은 조정이다 */
  subscriptionBillingItemId: z.number().nullable(),
  subscriptionId: z.number().nullable(),
})
export type BillingAdjustment = z.infer<typeof BillingAdjustmentSchema>

// 요금 항목 공통. 청구서가 아직 없는(예상) 달은 billingItemId 가 null 이고, adjustedChargeAmount 는 chargeAmount 에
// 항목에 붙은 조정을 더한 값이다.
const _billingItemShape = {
  billingItemId: z.number().nullable(),
  preDiscountAmount: z.number(),
  discountAmount: z.number(),
  chargeAmount: z.number(),
  adjustments: z.array(BillingAdjustmentSchema),
  adjustedChargeAmount: z.number(),
}

export const BillingRegistrationFeeSchema = z.object(_billingItemShape)
export type BillingRegistrationFee = z.infer<typeof BillingRegistrationFeeSchema>

export const BillingBaseFeeSchema = z.object({
  ..._billingItemShape,
  /** 월 기준 판매가. 일할이 없으면 preDiscountAmount 와 같다 */
  salePrice: z.number(),
  promotionTitle: z.string().nullable(),
  /** 정가면 null 이다 */
  promotionFromDate: z.iso.date().nullable(),
  promotionToDate: z.iso.date().nullable(),
  /** "19/30일 적용" 의 분자·분모. 일할이 없으면 null 이다 */
  prorateDays: z.number().nullable(),
  prorateDenominator: z.number().nullable(),
})
export type BillingBaseFee = z.infer<typeof BillingBaseFeeSchema>

export const BillingTierSchema = z.object({
  from: z.number(),
  /** null 이면 상한이 없다 */
  upTo: z.number().nullable(),
  /** 학생당 단가 */
  unitPrice: z.number(),
})
export type BillingTier = z.infer<typeof BillingTierSchema>

export const BillingStudentCountRuleSchema = z.object({
  /** 학생을 과금 대상으로 세는 기준. NUMBER_OF_ACTIVE_DAYS(활동일수)·NUMBER_OF_SOLVED_PROBLEM(채점 문항 수) */
  criterion: z.string(),
  /** 이 값 이상이면 과금 대상으로 센다 */
  threshold: z.number(),
})
export type BillingStudentCountRule = z.infer<typeof BillingStudentCountRuleSchema>

export const BillingUsagePricingSchema = z.object({
  /** 무료 수량은 단가 0원인 첫 구간이다 */
  tiers: z.array(BillingTierSchema),
  /** 학생당 과금(PER_STUDENT)에만 있다 */
  studentCountRule: BillingStudentCountRuleSchema.nullable(),
  /** 단가 0원 구간의 상한. 무료 구간이 없으면 0 이다 */
  freeStudentCount: z.number(),
})
export type BillingUsagePricing = z.infer<typeof BillingUsagePricingSchema>

export const BillingUsageSchema = z.object({
  ..._billingItemShape,
  category: z.string(),
  /** 과금 분류 한글명. 모르는 분류는 이 이름을 그대로 보여 준다 */
  categoryDescription: z.string(),
  quantity: z.number().nullable(),
  /** 청구 시점에 동결된 산정 근거. 근거가 없는 과거 청구는 null 이다 */
  pricing: BillingUsagePricingSchema.nullable(),
})
export type BillingUsage = z.infer<typeof BillingUsageSchema>

/** 청구서 한 건. 예상(청구서 미생성)이면 billingId·billingType·status 가 null 이다 */
export const BillingSectionSchema = z.object({
  billingId: z.number().nullable(),
  /** REGULAR(정기)·PRORATED(개시·변경 안분)·FINAL(해지 최종 청구) */
  billingType: z.string().nullable(),
  /** DRAFT·PENDING·PAID·FAILED */
  status: z.string().nullable(),
  planTitle: z.string().nullable(),
  /** BASIC·PREMIUM·SOLO·SOLO_PLUS 등. 계약이 사라진 청구는 null 이다 */
  planTier: z.string().nullable(),
  prepaidFromDate: z.iso.date().nullable(),
  prepaidToDate: z.iso.date().nullable(),
  postpaidFromDate: z.iso.date().nullable(),
  postpaidToDate: z.iso.date().nullable(),
  /** 항목별 조정 반영 금액의 합에 항목에 붙지 않은 조정을 더한 값 */
  totalAmount: z.number(),
  registrationFees: z.array(BillingRegistrationFeeSchema),
  baseFees: z.array(BillingBaseFeeSchema),
  usages: z.array(BillingUsageSchema),
  /** 요금 항목에 붙지 않은 조정만 담는다. 항목에 붙은 조정은 각 항목 안에 있다 */
  adjustments: z.array(BillingAdjustmentSchema),
})
export type BillingSection = z.infer<typeof BillingSectionSchema>

export const BillingSchema = z.object({
  /** true 면 청구서가 아직 없는 예상 금액이다 */
  estimated: z.boolean(),
  /** 청구 항목 합과 조정 합. 미수금(debt)은 넣지 않는다 */
  totalAmount: z.number(),
  /** 결제일(매월 며칠) */
  payDate: z.number(),
  payMethod: z.string().nullable(),
  /** 이번 건이 실제로 청구되는 날 */
  billingDate: z.iso.date(),
  debt: z.number(),
  /** 2건 이상이면 그 달에 요금제가 바뀌었다. 최근 청구서가 앞에 온다 */
  billings: z.array(BillingSectionSchema),
})
export type Billing = z.infer<typeof BillingSchema>

/***************** 학원 월 청구 *****************/
// 학원의 한 달 이용 요금. additionalPricingPlan 부터 activeStudentCount 까지는 추가 과금 조건과 그 결과이고
// 추가 과금이 없는 학원은 null 이다. 받지 않는 필드: authority(그 달 값이 아니라 조회 시점의 학원 등급이고
// 화면이 읽지 않는다).
export const AcademyPaymentSchema = z.object({
  subscribePlan: SubscribePlanSchema,
  /** 추가 과금 기준. 채점 문항 수(베이직) 또는 활동일수(뉴베이직·프리미엄) */
  additionalPricingPlan: AdditionalPricingPlanSchema.nullable(),
  /** 과금 대상으로 세는 기준값(채점 문항 수 또는 활동일수) */
  additionalPricingPlanStandardValue: z.number().nullable(),
  /** 추가 과금 없이 쓸 수 있는 학생 수 */
  additionalPricingPlanFreeStudentCount: z.number().nullable(),
  additionalPricingPlanFeeOnEachStudent: z.number().nullable(),
  activeStudentCount: z.number().nullable(),
  /** 기간 안에 추가 과금된 학생 수. 추가 과금이 없는 학원은 0 이다 */
  additionalStudentCount: z.number(),
  subscribeFee: z.number(),
  additionalFee: z.number(),
  /** HWP 학습지 다운로드 요금. paymentAmount 에 들어 있다 */
  additionalHwpFee: z.number(),
  /** HWP 로 내려받은 문항 수 */
  hwpProblemCount: z.number(),
  /** 과금 기간 */
  invoiceStartDatetime: _localDatetime,
  invoiceEndDatetime: _localDatetime,
  /** 결제 기준일 */
  paymentDate: z.iso.date(),
  paymentAmount: z.number(),
  /** 미수금 */
  debt: z.number(),
  // billing 서버로 옮기기 전 학원·조회 가능 기간 밖 달은 null 이고, 그때는 위 금액 필드로 그린다.
  billing: BillingSchema.nullable(),
})
export type AcademyPayment = z.infer<typeof AcademyPaymentSchema>

/***************** 학생별 사용량 *****************/
export const PaymentStudentSchema = z.object({
  /** 목록 순번 */
  index: z.number(),
  studentId: StudentSchema.shape.id,
  academyId: AcademySchema.shape.academyId,
  studentName: StudentSchema.shape.name,
  studentSchoolType: StudentSchema.shape.schoolType,
  studentGrade: StudentSchema.shape.grade,
  studentStatus: StudentSchema.shape.status,
  /** 채점한 문항 수 */
  solveCount: z.number(),
  /** 활동일수 */
  activeDateCount: z.number(),
})
export type PaymentStudent = z.infer<typeof PaymentStudentSchema>

/***************** 문자 요금 *****************/
// 받지 않는 필드: payStartYearMonth(문자 과금 시작 연월. 화면이 읽지 않는다).
export const PaymentMessageSchema = z.object({
  /** 과금 기간과 결제일(매월 며칠) */
  base: z.object({
    startDate: z.iso.date(),
    endDate: z.iso.date(),
    payDate: z.number(),
  }),
  summary: z.object({
    smsCount: z.number(),
    lmsCount: z.number(),
    smsFee: z.number(),
    lmsFee: z.number(),
  }),
  messages: sliceSchema(SmsSchema),
})
export type PaymentMessage = z.infer<typeof PaymentMessageSchema>
